// PersistMiddlewareLoader.jsx
'use client';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { persist as persistDebug } from '../resource/debug.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';

const log = persistDebug.extend('log:PersistMiddlewareLoader');
const realUserEvents = ['mousemove', 'click', 'scroll'];

// for persist sensitive actions, we need to wait for
// restoreHook.promise to complete before we can do the action
export const restoreHook = {
  promise: null,
  resolve: null,
};

export const PersistMiddlewareLoader = ({
  userId = '',
  isBrowserTabVisible = true,
  refreshToken = '',
  restoreRedux = NULL_FUNCTION,
  persistNow = NULL_FUNCTION,
  postMessageToApp = NULL_FUNCTION,
}) => {
  const userIdRef = useRef(userId);
  const isAttachedRef = useRef(false);
  const prevRefreshToken = useRef(refreshToken);
  const dedupRestorePromiseRef = useRef(null);

  const attachListeners = () => {
    if (!isAttachedRef.current) {
      isAttachedRef.current = true;
      window.addEventListener('pageshow', pageshowHandler);
      window.addEventListener('pagehide', pagehideHandler);
      window.addEventListener('focus', focusHandler);
      window.addEventListener('blur', blurHandler);
      log('attach listeners');
    }
  };

  const detachListeners = () => {
    window.removeEventListener('pageshow', pageshowHandler);
    window.removeEventListener('pagehide', pagehideHandler);
    window.removeEventListener('focus', focusHandler);
    window.removeEventListener('blur', blurHandler);
    log('detach listeners');
  };

  const visibilityChangeHandler = async isBrowserTabVisible => {
    log('visibilitychange', isBrowserTabVisible);
    if (isBrowserTabVisible) {
      if (!restoreHook.promise) {
        const { promise, resolve } = Promise.withResolvers();
        restoreHook.promise = promise;
        restoreHook.resolve = resolve;
      }
      await restoreReduxFromIndexedDB();
      addPersistMiddleware();
    } else removePersistMiddleware();
  };

  const pageshowHandler = async () => {
    if (!document.hidden) {
      log('page show');
      if (!restoreHook.promise) {
        const { promise, resolve } = Promise.withResolvers();
        restoreHook.promise = promise;
        restoreHook.resolve = resolve;
      }
      await restoreReduxFromIndexedDB();
      addPersistMiddleware();
    }
  };

  const pagehideHandler = async () => {
    if (document.hidden) {
      log('page hide');
      removePersistMiddleware();
    }
  };

  const focusHandler = async () => {
    log('on focus');
    if (!restoreHook.promise) {
      const { promise, resolve } = Promise.withResolvers();
      restoreHook.promise = promise;
      restoreHook.resolve = resolve;
    }
    await restoreReduxFromIndexedDB();
    addPersistMiddleware();
  };

  const blurHandler = async () => {
    log('on blur');
    removePersistMiddleware();
  };

  const restoreReduxFromIndexedDB = async () => {
    // we have multiple restoreReduxFromIndexedDB calls for different events,
    // so we need to dedup them
    if (dedupRestorePromiseRef.current)
      return await dedupRestorePromiseRef.current;

    log('restore redux from indexedDB');
    const { promise, resolve } = Promise.withResolvers();
    dedupRestorePromiseRef.current = promise;

    const { loadState } = await import('../resource/persist.js');
    const storageState = await loadState();
    await restoreRedux(storageState);
    resolve();
    dedupRestorePromiseRef.current = null;

    log('restore redux from indexedDB done');
    restoreHook.resolve?.();
    restoreHook.resolve = null;
    restoreHook.promise = null;
  };

  const addPersistMiddleware = async () => {
    const [{ addMiddleware, hasMiddlewareExisted }, { persistMiddleware }] =
      await Promise.all([
        import('../middleware/dynamicMiddleware.js'),
        import('../middleware/persist.js'),
      ]);
    if (!hasMiddlewareExisted(persistMiddleware)) {
      addMiddleware(persistMiddleware);
      log('add persist middleware');
    } else log('already loaded');
  };

  const removePersistMiddleware = async () => {
    const [{ removeMiddleware, hasMiddlewareExisted }, { persistMiddleware }] =
      await Promise.all([
        import('../middleware/dynamicMiddleware.js'),
        import('../middleware/persist.js'),
      ]);

    if (hasMiddlewareExisted(persistMiddleware)) {
      removeMiddleware(persistMiddleware);
      log('remove persist middleware');
    } else log('already unloaded');
  };

  const syncPersist = async () => {
    log('syncPersist');
    await persistNow();
    log('persist complete');

    const [{ sendMessageToSW }, { RESTORE_REDUX }] = await Promise.all([
      import('../serviceWorker/helpers.js'),
      import('../serviceWorker/AppMessageTypes.js'),
    ]);
    sendMessageToSW({ type: RESTORE_REDUX });
    log('send message to sw to sync persist');
  };

  const initPersistMiddleware = () => {
    log('init persist middleware');
    realUserEvents.forEach(event => {
      window.removeEventListener(event, initPersistMiddleware);
    });

    requestIdleCallback(async () => {
      await restoreReduxFromIndexedDB();
      await addPersistMiddleware();
      attachListeners();
    });
  };

  const syncRefreshTokenForWebview = async () => {
    if (getIsOnWebviewApp()) {
      await persistNow();
      postMessageToApp({
        message: 'onRefreshTokenUpdate',
        messageArgs: [refreshToken || null],
      });
    }
  };

  useEffect(() => {
    realUserEvents.forEach(event => {
      window.addEventListener(event, initPersistMiddleware);
    });
    return () => {
      isAttachedRef.current = false;
      detachListeners();
      realUserEvents.forEach(event => {
        window.removeEventListener(event, initPersistMiddleware);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    visibilityChangeHandler(isBrowserTabVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrowserTabVisible]);

  useEffect(() => {
    // login
    if (!userIdRef.current && userId) {
      // The tab that does the login action should set the shouldSyncPersist flag,
      // so that other tabs won't need to do syncPersist after restoring redux
      // sync persist when there is no shouldSyncPersist flag in the begging or shouldSyncPersist flag is 'true'
      const shouldSyncPersist = localStorage.getItem('shouldSyncPersist');
      if (shouldSyncPersist === null || shouldSyncPersist === 'true') {
        log('syncPersist because the userId is set');
        syncPersist();
        localStorage.setItem('shouldSyncPersist', false);
      }
    }

    // logout
    if (userIdRef.current && !userId) {
      // set shouldSyncPersist to true for next login action
      localStorage.setItem('shouldSyncPersist', true);
    }

    userIdRef.current = userId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (prevRefreshToken.current !== refreshToken) {
      prevRefreshToken.current = refreshToken;
      syncRefreshTokenForWebview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  return null;
};

PersistMiddlewareLoader.propTypes = {
  userId: PropTypes.string,
  refreshToken: PropTypes.string,
  isBrowserTabVisible: PropTypes.bool,
  restoreRedux: PropTypes.func,
  persistNow: PropTypes.func,
  postMessageToApp: PropTypes.func,
};

export default PersistMiddlewareLoader;
